import { useDispatch, useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { ExportVariantsDropdownItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { initExportToCrmAs } from "app/pages/sales-intelligence/sub-modules/company-search/store/action-creators";

import ExportToSalesforceDropdownItem from "../../components/ExportToSalesforceDropdownItem/ExportToSalesforceDropdownItem";
import {
    selectIsCompanyAsLeadExportClaimEnabled,
    selectIsSalesforceCompanyAsLeadExportEnabled,
    selectIsSalesforceExportEnabled,
    selectSalesforceStatus,
} from "../../store/selectors/salesforceSelectors";
import {
    EXPORT_SALESFORCE_BTN,
    EXPORT_INTEGRATION_TOOLTIP,
    EXPORT_SALESFORCE_ACCOUNT_BTN,
    EXPORT_SALESFORCE_LEAD_BTN,
    CONNECT_TO_INTEGRATION_TOOLTIP,
    ADMIN_ENABLE_EXPORT_INTEGRATION_TOOLTIP,
    CRM_IS_NOT_CONNECTED_TOOLTIP,
} from "../../translations/ExportItem";
import { CrmListObjectType } from "../../types";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

type WebsitesSalesforceExportItemContainerProps = {
    onClick: () => void;
    tooltipDisabledContent?: string;
    isUseLockedIcon?: boolean;
};

const WebsitesSalesforceExportItemContainer = ({
    onClick,
    tooltipDisabledContent,
    isUseLockedIcon = true,
}: WebsitesSalesforceExportItemContainerProps) => {
    const dispatch = useDispatch();
    const syncStatus = useSelector(selectSalesforceStatus);
    const isExportEnabled = useSelector(selectIsSalesforceExportEnabled);
    const isCompanyAsLeadExportClaimEnabled = useSelector(selectIsCompanyAsLeadExportClaimEnabled);
    const isCompanyAsLeadExportEnabled = useSelector(selectIsSalesforceCompanyAsLeadExportEnabled);

    const redirectToManagementPage = () => {
        void siNavigator.goToManagementPage({
            isScrollToIntegrationSection: true,
        });
    };

    const isAdmin = salesSettingsHelper.isAdmin();

    const handleExportAccount = () => {
        dispatch(initExportToCrmAs(CrmListObjectType.Accounts));
        onClick();
    };

    const handleExportLead = () => {
        dispatch(initExportToCrmAs(CrmListObjectType.Leads));
        onClick();
    };

    if (tooltipDisabledContent) {
        return (
            <ExportVariantsDropdownItem
                iconName={ExportIntegrations.salesforce}
                isDisabled
                variant={ExportIntegrations.salesforce}
                text={translate(EXPORT_SALESFORCE_BTN)}
                tooltipDisabledContent={tooltipDisabledContent}
                isUseLockedIcon={isUseLockedIcon}
            />
        );
    }

    if (syncStatus === "NotIntegrated") {
        if (isAdmin) {
            return (
                <ExportVariantsDropdownItem
                    iconName={ExportIntegrations.salesforce}
                    isDisabled={false}
                    variant={ExportIntegrations.salesforce}
                    text={translate(CONNECT_TO_INTEGRATION_TOOLTIP, { integrationName: "Salesforce" })}
                    isUseLockedIcon={isUseLockedIcon}
                    onClick={redirectToManagementPage}
                />
            );
        } else {
            return (
                <ExportVariantsDropdownItem
                    iconName={ExportIntegrations.salesforce}
                    isDisabled={true}
                    variant={ExportIntegrations.salesforce}
                    text={translate(EXPORT_SALESFORCE_ACCOUNT_BTN)}
                    isUseLockedIcon={isUseLockedIcon}
                    tooltipDisabledContent={translate(CRM_IS_NOT_CONNECTED_TOOLTIP)}
                />
            );
        }
    }

    if (isAdmin && !isExportEnabled) {
        return (
            <ExportVariantsDropdownItem
                iconName={ExportIntegrations.salesforce}
                isDisabled={false}
                variant={ExportIntegrations.salesforce}
                text={translate(EXPORT_SALESFORCE_BTN)}
                isUseLockedIcon={isUseLockedIcon}
                tooltipContent={translate(ADMIN_ENABLE_EXPORT_INTEGRATION_TOOLTIP)}
                onClick={redirectToManagementPage}
            />
        );
    }

    if (!isExportEnabled && !isCompanyAsLeadExportEnabled) {
        return (
            <ExportVariantsDropdownItem
                iconName={ExportIntegrations.salesforce}
                isDisabled
                variant={ExportIntegrations.salesforce}
                text={translate(EXPORT_SALESFORCE_BTN)}
                tooltipDisabledContent={translate(EXPORT_INTEGRATION_TOOLTIP, {
                    integrationName: "Salesforce",
                })}
                isUseLockedIcon={isUseLockedIcon}
            />
        );
    }

    if (isExportEnabled && isCompanyAsLeadExportClaimEnabled && isCompanyAsLeadExportEnabled) {
        return (
            <>
                <ExportToSalesforceDropdownItem
                    text={`${translate(EXPORT_SALESFORCE_BTN)}:`}
                    isUseLockedIcon={isUseLockedIcon}
                />
                <ExportVariantsDropdownItem
                    variant="accounts"
                    text={translate(EXPORT_SALESFORCE_ACCOUNT_BTN)}
                    onClick={handleExportAccount}
                    isUseLockedIcon={isUseLockedIcon}
                />
                <ExportVariantsDropdownItem
                    variant="leads"
                    text={translate(EXPORT_SALESFORCE_LEAD_BTN)}
                    onClick={handleExportLead}
                    isUseLockedIcon={isUseLockedIcon}
                />
            </>
        );
    }

    return (
        <ExportVariantsDropdownItem
            iconName={ExportIntegrations.salesforce}
            variant={ExportIntegrations.salesforce}
            text={translate(EXPORT_SALESFORCE_BTN)}
            onClick={
                isCompanyAsLeadExportClaimEnabled && isCompanyAsLeadExportEnabled
                    ? handleExportLead
                    : handleExportAccount
            }
            isUseLockedIcon={isUseLockedIcon}
        />
    );
};

export default WebsitesSalesforceExportItemContainer;
